/* eslint-disable import/no-duplicates */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { Chart } from 'react-google-charts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCurrentEffect } from '../../../../../../lib/use-current-effect';

import { usePatient } from '../../../../../../hooks/patient';
import { getMeasurementsByDay } from '../../../../../../services/metrics';
import { MetricTypeEnum } from '../../../../../../enums/MetricTypeEnum';

import MeasurementsArea from '../../MeasurementsArea';

import { ChildrenProps, DataChildrenProps, ChildrenRefProps } from '../..';

const RespiratoryRateChart: React.ForwardRefRenderFunction<
  ChildrenRefProps,
  ChildrenProps
> = ({ dateFilter }, ref) => {
  const { patient } = usePatient();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataChildrenProps>();
  const [breathingRateGraphData, setBreathingRateGraphData] = useState<
    (string | number)[][]
  >([['00:00', 0]]);

  const handleChange = useCallback(
    async (patientId?: string, isCurrent = () => true) => {
      setIsLoading(true);
      if (!patientId) {
        return;
      }
      const response = await getMeasurementsByDay({
        patientId,
        metricType: MetricTypeEnum.RESPIRATORY_RATE,
        dateFilter,
      });
      if (response.length > 0) {
        const lastRecordTime = format(
          new Date(response[response.length - 1].mensuramentDate),
          "dd'/'MM'/'yyyy', ' HH'h'mm",
          { locale: ptBR },
        );

        const result = response.map(({ respiratoryRate, mensuramentDate }) => [
          format(new Date(mensuramentDate), 'HH:mm', {
            locale: ptBR,
          }),
          respiratoryRate,
        ]);

        const value = result[result.length - 1][1].toString();

        if (isCurrent()) {
          setBreathingRateGraphData([...result]);
          setData({
            lastRecordTime,
            value,
          });
        }
      } else if (isCurrent()) {
        setBreathingRateGraphData([['00:00', 0]]);
        setData(undefined);
      }

      setIsLoading(false);
    },
    [dateFilter],
  );

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          handleChange(patient?.id, isCurrent);
        } else {
          setIsLoading(false);
        }
      })();
    },
    [dateFilter, handleChange, patient],
  );

  useImperativeHandle(ref, () => ({
    reload: () => handleChange(patient?.id),
  }));

  return (
    <MeasurementsArea
      data={{
        styleData: 'chart',
        isLoading,
        cardTitle: 'Movimentos respiratórios por minuto',
        dataDetails: data
          ? {
              title: 'último registro:',
              position: 'center',
              lastRecordTime: data.lastRecordTime,
              type: 'mrm',
              value: data.value,
              inAlert: false,
            }
          : undefined,
      }}
    >
      <Chart
        width="107%"
        height="110%"
        chartType="LineChart"
        style={{
          marginLeft: '-10px',
          marginTop: '-50px',
        }}
        options={{
          legend: 'none',
          colors: ['#E12F54'],
          vAxis: {
            ticks: [12, 16, 18, 20, 22, 25, 28, 30, 37, 40, 50, 53, 60],
          },
          series: {
            0: { curveType: 'function' },
          },
        }}
        data={[['h', 'mrm'], ...breathingRateGraphData]}
        rootProps={{ 'data-testid': '8' }}
      />
    </MeasurementsArea>
  );
};

export default forwardRef(RespiratoryRateChart);
